import type { FloorLine, FloorRep, LineStats, WaitingCaller } from "@/lib/types";

/**
 * The live floor's arithmetic: which reps are on a line and in what order, what the tiles count, how
 * long a caller has waited, and the window a line's history is counted over. Pure, for
 * `test/floor.test.ts`.
 */

/** The windows tap-ucaas's queue monitor offers. */
export const STATS_WINDOWS = [
  { id: "1h", label: "Last hour" },
  { id: "today", label: "Today" },
  { id: "24h", label: "Last 24 hours" },
  { id: "7d", label: "Last 7 days" },
  { id: "30d", label: "Last 30 days" },
] as const;

export type StatsWindow = (typeof STATS_WINDOWS)[number]["id"];

const WINDOW_HOURS: Record<Exclude<StatsWindow, "today">, number> = {
  "1h": 1,
  "24h": 24,
  "7d": 24 * 7,
  "30d": 24 * 30,
};

/**
 * Where a window starts, as `/acd/stats` takes it — on the minute, so the query's key holds still
 * for a minute rather than changing on every render. "Today" starts at midnight on this browser's
 * clock: the supervisor's day, not the server's.
 */
export function windowStart(window: StatsWindow, now: Date): string {
  if (window === "today") {
    const midnight = new Date(now);
    midnight.setHours(0, 0, 0, 0);
    return midnight.toISOString();
  }
  const start = new Date(now.getTime() - WINDOW_HOURS[window] * 3_600_000);
  start.setSeconds(0, 0);
  return start.toISOString();
}

/** The line a floor shows: the one picked, while it still exists, else the first — the same rule
 *  tap-ucaas's monitor keeps, so the page never jumps to another line as callers come and go. */
export function currentLine(
  lines: readonly Pick<FloorLine, "name">[],
  picked: string | null,
): string | null {
  if (picked !== null && lines.some((line) => line.name === picked)) return picked;
  return lines[0]?.name ?? null;
}

/** The team a floor narrows to: the one picked, while it still exists, else none (null), which
 *  shows every rep. */
export function currentTeam(teams: readonly { id: number }[], picked: number | null): number | null {
  return picked !== null && teams.some((team) => team.id === picked) ? picked : null;
}

/** The reps in `team` now (ADR-195 in platform), by the team ids the floor carries on each; null is
 *  every rep, in a team or not. */
export function repsInTeam(reps: readonly FloorRep[], team: number | null): FloorRep[] {
  return team === null ? [...reps] : reps.filter((rep) => (rep.teams ?? []).includes(team));
}

export function lineTitle(line: Pick<FloorLine, "name" | "campaign_name">): string {
  return line.campaign_name ? `${line.campaign_name} · ${line.name}` : line.name;
}

/** `EFFECTIVE_STATE_ORDER` in `services/shared/state/queues.py`: the most call-engaged first. */
const STATE_ORDER: readonly string[] = [
  "busy", "ringing", "busy_external", "dialing", "disposition", "wrapup", "paused", "available",
  "offline",
];

function stateRank(state: string): number {
  const at = STATE_ORDER.indexOf(state);
  return at === -1 ? STATE_ORDER.length : at;
}

export function repName(rep: Pick<FloorRep, "displayName" | "user">): string {
  return rep.displayName ?? rep.user;
}

/** A rep's row, the same across refetches. */
export function repKey(rep: Pick<FloorRep, "user" | "domain">): string {
  return `${rep.user}@${rep.domain}`;
}

/**
 * Where a rep's time in their state counts from, and what it counts. A rep the dialer is calling
 * for starts a new `dialing` with every dial, so theirs counts from the end of their last call,
 * or from signing in before their first (ACL-517); everyone else's from when their state began.
 */
export function stateClock(
  rep: Pick<FloorRep, "state" | "since" | "lastCallEndedAt" | "signedInAt">,
): { from: string | null; label: "since last call" | "since signing in" | null } {
  if (rep.state === "dialing") {
    if (rep.lastCallEndedAt) return { from: rep.lastCallEndedAt, label: "since last call" };
    if (rep.signedInAt) return { from: rep.signedInAt, label: "since signing in" };
  }
  return { from: rep.since ?? null, label: null };
}

/** The columns a supervisor can sort the Reps table on. */
export type RepSortKey = "rep" | "state" | "time" | "handled";
export interface RepSort {
  key: RepSortKey;
  dir: "asc" | "desc";
}

/** On a call first, the longest call on top; then about to be, then free — the order a supervisor
 *  reads a floor in. */
export const DEFAULT_REP_SORT: RepSort = { key: "state", dir: "asc" };

/** The direction a column sorts in on its first click: names A to Z and states most engaged
 *  first, times and counts largest first. */
export function firstDirection(key: RepSortKey): RepSort["dir"] {
  return key === "rep" || key === "state" ? "asc" : "desc";
}

const REP_SORT_KEYS: readonly RepSortKey[] = ["rep", "state", "time", "handled"];

/** A stored sort, or the default for anything else (an older or hand-edited value). */
export function parseRepSort(raw: unknown): RepSort {
  if (raw && typeof raw === "object") {
    const { key, dir } = raw as Record<string, unknown>;
    if (REP_SORT_KEYS.includes(key as RepSortKey) && (dir === "asc" || dir === "desc")) {
      return { key: key as RepSortKey, dir };
    }
  }
  return DEFAULT_REP_SORT;
}

function clockMillis(rep: FloorRep): number | null {
  const from = stateClock(rep).from;
  const at = from ? Date.parse(from) : Number.NaN;
  return Number.isNaN(at) ? null : at;
}

/** Longest in their state first; a rep with no clock last. On the clock's start, never on the
 *  seconds shown, so the order holds still while the clocks count up. */
function byLongest(a: FloorRep, b: FloorRep): number {
  const x = clockMillis(a);
  const y = clockMillis(b);
  if (x === null || y === null) return x === y ? 0 : x === null ? 1 : -1;
  return x - y;
}

const byName = (a: FloorRep, b: FloorRep) =>
  repName(a).localeCompare(repName(b)) || a.user.localeCompare(b.user);

/**
 * The reps in `sort`'s order. By state: the most engaged state first and, inside each, the rep who
 * has been in it longest first, so a rep whose state changes goes to the bottom of their new group
 * and the rows above stay where they are. Every other column falls back to that order on a tie.
 */
export function sortReps(reps: readonly FloorRep[], line: string, sort: RepSort): FloorRep[] {
  const flip = sort.dir === "desc" ? -1 : 1;
  const byState = (a: FloorRep, b: FloorRep) => stateRank(a.state) - stateRank(b.state);
  const handled = (rep: FloorRep) => callsOnLine(rep, line) ?? -1;
  const compare = (a: FloorRep, b: FloorRep): number => {
    switch (sort.key) {
      case "rep":
        return flip * byName(a, b);
      case "state":
        return flip * byState(a, b) || byLongest(a, b) || byName(a, b);
      case "time":
        return -flip * byLongest(a, b) || byState(a, b) || byName(a, b);
      case "handled":
        return flip * (handled(a) - handled(b)) || byState(a, b) || byLongest(a, b) || byName(a, b);
    }
  };
  return [...reps].sort(compare);
}

/** The reps signed in to `line`, in the floor's own order (`DEFAULT_REP_SORT`). */
export function repsOnLine(reps: readonly FloorRep[], line: string): FloorRep[] {
  return sortReps(
    reps.filter((rep) => rep.queues.includes(line)),
    line,
    DEFAULT_REP_SORT,
  );
}

/**
 * The rows as they stood (`shown`, by `repKey`), with `reps` read into them: while the supervisor's
 * pointer is on the table or a row's menu is open, nobody moves. A rep who left drops out, and a rep
 * who signed in meanwhile joins at the bottom, in `reps`' order.
 */
export function keepOrder(shown: readonly string[], reps: readonly FloorRep[]): FloorRep[] {
  const at = new Map(shown.map((key, i) => [key, i]));
  const kept = reps.filter((rep) => at.has(repKey(rep)));
  kept.sort((a, b) => (at.get(repKey(a)) ?? 0) - (at.get(repKey(b)) ?? 0));
  return [...kept, ...reps.filter((rep) => !at.has(repKey(rep)))];
}

/** The calls a rep finished on `line` since signing in to it. The ACD counts per line, so this is the
 *  line's own count and never another line's; `null` for a record from before per-line counts. */
export function callsOnLine(
  rep: Pick<FloorRep, "callsHandledByQueue">,
  line: string,
): number | null {
  return rep.callsHandledByQueue?.[line] ?? null;
}

const ON_A_CALL = new Set(["ringing", "busy", "busy_external", "dialing"]);

/** Somebody on a call, as the floor names them; `leadId` is the contact to link to. */
export interface CallParty {
  name: string | null;
  number: string | null;
  leadId: string | null;
}

/**
 * A caller as the floor names them: the contact's own name where the call is a contact's (the
 * switch names a Twilio caller only by number), else the name the switch gave, and the number.
 */
export function callParty(
  row: Pick<WaitingCaller, "contactName" | "callerName" | "callerNumber" | "leadId">,
): CallParty {
  const name = row.contactName?.trim() || row.callerName?.trim() || null;
  const number = row.callerNumber?.trim() || null;
  // A switch with no name for the caller often sends their number as one.
  const sameAsNumber = name !== null && number !== null && digits(name) === digits(number);
  return { name: sameAsNumber ? null : name, number, leadId: row.leadId ?? null };
}

/** Who a rep is on the phone with: ringing or talking on a line's call, in or out, dialing a lead
 *  or on a call of their own. Nobody while they are off the phone. */
export function repParty(
  rep: Pick<FloorRep, "state" | "contactName" | "callerName" | "callerNumber" | "leadId">,
): CallParty | null {
  return ON_A_CALL.has(rep.state) ? callParty(rep) : null;
}

function digits(text: string): string {
  return text.replace(/\D/g, "").slice(-10);
}

const WRAPPING_UP = new Set(["disposition", "wrapup"]);
/** Talking with somebody, in or out: the On a call tile. A rep whose phone rings, or for whom the
 *  dialer is placing a call, is not on one yet. The reports keep the two apart the same way
 *  (`STATE_GROUPS` in `lib/reports.ts`). */
const TALKING = new Set(["busy", "busy_external"]);
const REACHING = new Set(["ringing", "dialing"]);

/** What the tiles count, over each rep's effective state. A rep busy on another of their lines is
 *  not ready for this one, and the ACD will not offer them its callers either. A free rep on My
 *  calls only on `line` is counted apart from the Ready ones (ADR-037): they take none of the line's
 *  calls but their own. */
export function repCounts(
  reps: readonly Pick<FloorRep, "state" | "ownCallsOnly">[],
  line?: string,
) {
  const mine = (rep: Pick<FloorRep, "ownCallsOnly">) =>
    line !== undefined && (rep.ownCallsOnly ?? []).includes(line);
  return {
    signedIn: reps.length,
    ready: reps.filter((rep) => rep.state === "available" && !mine(rep)).length,
    mine: reps.filter((rep) => rep.state === "available" && mine(rep)).length,
    onCall: reps.filter((rep) => TALKING.has(rep.state)).length,
    reaching: reps.filter((rep) => REACHING.has(rep.state)).length,
    wrappingUp: reps.filter((rep) => WRAPPING_UP.has(rep.state)).length,
    onBreak: reps.filter((rep) => rep.state === "paused").length,
  };
}

/**
 * A caller who dialed a rep's own number, as the floor says it (ADR-037): whose number, and until
 * when the line holds them for that rep — "For Maria Lopez · anyone may take them in 0:12", or
 * "Only Maria Lopez · ends in 1:48" when nobody else may. Null for any other caller. `names` maps
 * `user@domain` to a rep's name.
 */
export function meantForText(
  caller: Pick<WaitingCaller, "meantFor" | "meantForOnly" | "pinnedUntil" | "kind">,
  names: ReadonlyMap<string, string>,
  now: number,
): string | null {
  if (!caller.meantFor || caller.kind === "handoff") return null;
  const name = names.get(caller.meantFor) ?? caller.meantFor.split("@")[0] ?? caller.meantFor;
  const until = caller.pinnedUntil ? Date.parse(caller.pinnedUntil) : Number.NaN;
  const left = Number.isNaN(until) ? null : Math.max(0, Math.ceil((until - now) / 1000));
  const clock = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  if (caller.meantForOnly) {
    return left === null ? `Only ${name}` : `Only ${name} · ends in ${clock(left)}`;
  }
  return left === null || left === 0
    ? `For ${name} · anyone may take them now`
    : `For ${name} · anyone may take them in ${clock(left)}`;
}

/** Whole seconds since `iso`, or null without a readable time. Never below zero: the switch's clock
 *  runs a little ahead of a browser's. */
export function secondsSince(iso: string | null | undefined, now: number): number | null {
  if (!iso) return null;
  const at = Date.parse(iso);
  return Number.isNaN(at) ? null : Math.max(0, Math.floor((now - at) / 1000));
}

/** The longest any of `callers` has waited, or null for an empty line. */
export function longestWait(
  callers: readonly Pick<WaitingCaller, "joinedAt">[],
  now: number,
): number | null {
  let longest: number | null = null;
  for (const caller of callers) {
    const waited = secondsSince(caller.joinedAt, now);
    if (waited !== null && (longest === null || waited > longest)) longest = waited;
  }
  return longest;
}

/** The callers in the order the line serves them: by the ACD's position where it gave one (1 is
 *  next), longest waiting first otherwise. */
export function callersInOrder(callers: readonly WaitingCaller[]): WaitingCaller[] {
  return [...callers].sort(
    (a, b) =>
      (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER) ||
      (a.joinedAt ?? "").localeCompare(b.joinedAt ?? ""),
  );
}

/** The callers a line served: CONNECTED, and DROP — reached a rep as the caller gave up
 *  (`src/acd_stats.py`). */
export function served(stats: Pick<LineStats, "connected" | "dropped">): number {
  return stats.connected + stats.dropped;
}

/** A 0–1 rate as a whole percentage, or a dash where there was nothing to divide. */
export function percent(rate: number | null | undefined): string {
  return rate === null || rate === undefined ? "—" : `${Math.round(rate * 100)}%`;
}

const CALL_KIND: Record<string, string> = { inbound: "Inbound", outbound: "Dialer", handoff: "Handoff" };

/** A call's kind in a word; one this console does not know is shown as the ACD spelled it. */
export function callKind(kind: string | null | undefined): string | null {
  return kind ? (CALL_KIND[kind] ?? kind) : null;
}

/**
 * Whether a supervisor can listen in on this rep's call from their own phone (ADR-029): the rep is
 * on a call the ACD connected, which lives in a conference room, and it is not the supervisor's own
 * call. core-api checks both again against the live records.
 */
export function canMonitor(rep: FloorRep, me: { user: string; domain: string } | null): boolean {
  if (rep.state !== "busy" || !rep.room || !rep.callerChannelId) return false;
  return me === null || rep.user !== me.user || rep.domain !== me.domain;
}

/**
 * Whether the floor offers to sign this rep out of the ACD (ADR-052 in `platform`): anybody but
 * the supervisor themselves, whose own phone has a Sign out of its own. core-api checks the reach
 * again, and refuses a rep who is not signed in at all.
 */
export function canSignOut(rep: FloorRep, me: { user: string; domain: string } | null): boolean {
  return me === null || rep.user !== me.user || rep.domain !== me.domain;
}

/** The rep is on a call right now, on any of their lines — a sign-out waits for it to end. */
export function onACall(rep: Pick<FloorRep, "state">): boolean {
  return ON_A_CALL.has(rep.state);
}
